import { useCallback, useEffect, useRef, useState } from "react";
import {
  RagDocument,
  fetchOrgDocuments,
  uploadOrgDocument,
  deleteDocument as apiDeleteDocument,
  retryDocument as apiRetryDocument,
  assignDocumentToAgent as apiAssign,
  unassignDocumentFromAgent as apiUnassign,
} from "../api/ragApi";

const POLL_INTERVAL = 5000;

export function useOrgDocuments() {
  const [documents, setDocuments] = useState<RagDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const docs = await fetchOrgDocuments();
      setDocuments(docs);
    } catch (err) {
      console.error("Failed to load org documents:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while any document is processing
  useEffect(() => {
    const hasProcessing = documents.some((d) => d.status === "processing");

    if (hasProcessing) {
      pollRef.current = setInterval(async () => {
        try {
          const docs = await fetchOrgDocuments();
          setDocuments(docs);
          if (!docs.some((d) => d.status === "processing") && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch {
          // Silently fail polling
        }
      }, POLL_INTERVAL);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [documents]);

  const tempIdRef = useRef(-1);

  const upload = useCallback(async (file: File) => {
    // Optimistic: show placeholder immediately
    const tempId = tempIdRef.current--;
    const placeholder: RagDocument = {
      id: tempId,
      agent_id: null,
      organization_id: 0,
      uploaded_by: 0,
      filename: file.name,
      s3_key: "",
      mime_type: file.type,
      size_bytes: file.size,
      chunk_count: 0,
      total_chunks_expected: 0,
      status: "processing",
      error_message: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      agents: [],
    };
    setDocuments((prev) => [placeholder, ...prev]);
    setUploading(true);

    try {
      const doc = await uploadOrgDocument(file);
      // Replace placeholder with real doc
      setDocuments((prev) => prev.map((d) => (d.id === tempId ? doc : d)));
    } catch (err) {
      // Remove placeholder on failure
      setDocuments((prev) => prev.filter((d) => d.id !== tempId));
      console.error("Failed to upload document:", err);
      throw err;
    } finally {
      setUploading(false);
    }
  }, []);

  const deleteDoc = useCallback(async (docId: number) => {
    try {
      await apiDeleteDocument(docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) {
      console.error("Failed to delete document:", err);
      throw err;
    }
  }, []);

  const retryDoc = useCallback(async (docId: number) => {
    try {
      const doc = await apiRetryDocument(docId);
      setDocuments((prev) => prev.map((d) => (d.id === docId ? doc : d)));
    } catch (err) {
      console.error("Failed to retry document:", err);
      throw err;
    }
  }, []);

  const assignToAgent = useCallback(
    async (docId: number, agentId: number) => {
      await apiAssign(docId, agentId);
      await load();
    },
    [load],
  );

  const unassignFromAgent = useCallback(
    async (docId: number, agentId: number) => {
      await apiUnassign(docId, agentId);
      await load();
    },
    [load],
  );

  return {
    documents,
    loading,
    uploading,
    upload,
    deleteDoc,
    retryDoc,
    assignToAgent,
    unassignFromAgent,
    reload: load,
  };
}
